import React, { useState, useEffect, useRef } from "react";
import Tooltip from "./Tooltip";

function getTimeRemaining(endtime: Date) {
  const t = endtime.getTime() - new Date().getTime();

  const seconds = Math.floor((t / 1000) % 60);
  const minutes = Math.floor((t / 1000 / 60) % 60);
  const hours = Math.floor((t / (1000 * 60 * 60)) % 24);
  const days = Math.floor(t / (1000 * 60 * 60 * 24));

  return {
    total: t,
    days,
    hours,
    minutes,
    seconds,
  };
}

function formatDetailed(t: ReturnType<typeof getTimeRemaining>): string {
  const parts: string[] = [];
  if (t.days > 0) {
    parts.push(`${t.days} day${t.days !== 1 ? "s" : ""}`);
  }
  if (t.hours > 0 || t.days > 0) {
    parts.push(`${t.hours} hour${t.hours !== 1 ? "s" : ""}`);
  }
  if (t.minutes > 0 || t.hours > 0 || t.days > 0) {
    parts.push(`${t.minutes} minute${t.minutes !== 1 ? "s" : ""}`);
  }
  parts.push(`${t.seconds} second${t.seconds !== 1 ? "s" : ""}`);
  if (parts.length === 0) {
    return "Really soon";
  }
  return parts.join(", ");
}

function formatMinimal(t: ReturnType<typeof getTimeRemaining>): string {
  if (t.days > 0) {
    return `${t.days} day${t.days > 1 ? "s" : ""}`;
  } else if (t.hours > 0) {
    return `${t.hours} hour${t.hours > 1 ? "s" : ""}`;
  } else if (t.minutes > 0) {
    return `${t.minutes} minute${t.minutes > 1 ? "s" : ""}`;
  } else {
    return "Really soon";
  }
}

export const Countdown = (props: {
  until: Date;
  text?: string;
  minimal?: boolean;
  /** Shows full breakdown (days, hours, minutes) with minimal styling (dashed underline + tooltip) */
  detailed?: boolean;
  dark?: boolean;
}) => {
  const { until, text = "", minimal, detailed, dark } = props;
  const [displayValue, setDisplayValue] = useState("");
  const [tooltipValue, setTooltipValue] = useState("");
  const intervalRef = useRef<number>();

  // React-based countdown for minimal/detailed modes
  useEffect(() => {
    if (!minimal && !detailed) return;

    function update() {
      const t = getTimeRemaining(until);
      if (t.total < 0) {
        setDisplayValue("Countdown over!");
        setTooltipValue("Countdown over!");
        return;
      }
      setTooltipValue(formatDetailed(t));
      if (detailed) {
        setDisplayValue(formatDetailed(t));
      } else {
        setDisplayValue(formatMinimal(t));
      }
    }

    update();
    intervalRef.current = window.setInterval(update, 1000);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [until, minimal, detailed]);

  const countdown = (element: HTMLElement): void => {
    function initializeClock(clock: HTMLElement, endtime: Date) {
      if (!clock) {
        return;
      }

      const daysSpan = clock.querySelector(".days")!;
      const hoursSpan = clock.querySelector(".hours")!;
      const minutesSpan = clock.querySelector(".minutes")!;
      const secondsSpan = clock.querySelector(".seconds")!;
      let timeinterval: number;

      function updateClock() {
        const t = getTimeRemaining(endtime);

        if (t.total < 0) {
          clock.innerHTML = "Countdown over!";
          return;
        }

        daysSpan.innerHTML = String(t.days);
        hoursSpan.innerHTML = ("0" + t.hours).slice(-2);
        minutesSpan.innerHTML = ("0" + t.minutes).slice(-2);
        secondsSpan.innerHTML = ("0" + t.seconds).slice(-2);

        if (t.total <= 0) {
          clearInterval(timeinterval);
        }
      }

      updateClock();
      timeinterval = setInterval(updateClock, 1000) as any;
    }

    initializeClock(element, until);
  };

  if (detailed || minimal) {
    return (
      <span className="border-dashed border-b dark:border-slate-500 border-slate-300">
        <Tooltip
          element={
            <span
              className={dark ? "font-semibold text-slate-900 dark:text-slate-400" : ""}
            >
              {displayValue}
            </span>
          }
          tooltip={tooltipValue}
        />
      </span>
    );
  } else {
    return (
      <div
        ref={(d) => d && countdown(d)}
        className="text-lg text-slate-700 dark:text-slate-200"
      >
        <span>
          <span className="days font-semibold"> </span> days{" "}
        </span>
        <span>
          <span className="hours font-semibold"> </span> hours{" "}
        </span>
        <span>
          <span className="minutes font-semibold"> </span> minutes{" "}
        </span>
        <span>
          <span className="seconds font-semibold"> </span> seconds{" "}
        </span>
        <span> {text} </span>
      </div>
    );
  }
};
